import type { Concept } from '@yuan-math/curriculum';

/**
 * Linear algebra: the second university branch out of Grade 12.
 *
 * Calculus branches out of `limits`. This one branches out of `vectors` — the
 * precalculus concept where an arrow in the plane first got components — and it
 * is the *other* thing school mathematics is a run-up to. A student who has
 * finished Grade 12 can take either; neither is a prerequisite of the other,
 * and nothing here depends on a derivative.
 *
 * The two roots are `vectors` and `systems-of-equations`, and that pairing is
 * the whole subject in miniature. Grade 8 solved two equations in two unknowns
 * by substitution and stopped, because substitution does not scale: three
 * equations is tedious and ten is impossible by hand. Precalculus drew arrows
 * and added them nose to tail and stopped, because two dimensions is where a
 * picture runs out. Linear algebra is what happens when you notice those are
 * the same subject — the columns of a system are vectors, and solving it is
 * asking whether one vector is reachable from the others.
 *
 * The orderings that are decisions rather than conventions:
 *
 * **`span` before `linear-independence`, and both before `matrices`.** The
 * usual first course opens with matrix arithmetic, because it is the part that
 * can be practised on day one. It is also the part that means nothing on day
 * one: a matrix is a table of numbers until there is a question it answers.
 * Here the questions come first — can I reach this vector from those? is one of
 * them redundant? — and the matrix arrives as the notation that makes them
 * computable. A student who meets matrix multiplication before span learns a
 * rule about rows and columns and never learns what it computes.
 *
 * **`row-reduction` separately from `gaussian-elimination`.** They are usually
 * one topic and they are two skills. Row reduction is an operation on a matrix:
 * three legal moves, run until the shape is echelon. Gaussian elimination is
 * what that is *for*: an augmented matrix, back-substitution, and — the part
 * students actually lose marks on — reading free variables off the pivots.
 * Taught as one topic the second half becomes a footnote to the arithmetic, and
 * "infinitely many solutions" gets answered with a shrug rather than with a
 * parametrised set.
 *
 * **`determinants` after `matrix-inverse`, not before.** Presented first, the
 * determinant is an unmotivated alternating sum that students memorise for 2×2
 * and fear for 3×3. Presented after the inverse has been computed by row
 * reduction — and after some matrices have refused to have one — it answers a
 * question the student is already holding: which matrices are invertible, and
 * is there a single number that says so.
 *
 * **`vector-spaces` after `solution-sets`, not at the start.** The axioms are
 * the traditional opening and the traditional place a course loses half its
 * room, because eight rules about an unspecified set are not mathematics a
 * beginner can hold. By the time a student arrives here they have seen ℝⁿ, the
 * span of a set, and the solution set of a homogeneous system, and all three
 * behave the same way under addition and scaling. The axioms are then a
 * summary of something noticed rather than a definition to be trusted.
 *
 * **`rank` depends on `linear-independence` as well as on `linear-systems`.**
 * Rank is defined here as the number of pivots, which is a fact about an
 * algorithm, and it is worth nothing until it is also the number of independent
 * columns. A course that defines rank as "the number of non-zero rows in the
 * echelon form" and stops has taught a way to compute a number that predicts
 * nothing.
 *
 * **`eigenvalues` requires `transformation-matrix`.** Av = λv is a statement
 * about a *transformation* — the directions it does not turn — and a student
 * who meets it as a determinant equation solves it correctly and cannot say
 * what was found. `determinants` supplies the technique; `transformation-matrix`
 * supplies the question.
 *
 * **`diagonalization` requires `basis`.** Diagonalising is not a factorisation
 * trick. It is a change of basis, and the reason it can fail is that the
 * eigenvectors may not amount to one. A course that presents P⁻¹AP as an
 * algorithm cannot explain why some matrices refuse.
 */
export const LINEAR_ALGEBRA_CONCEPTS: Concept[] = [
  /* ------------------------------------------ vectors, and what they make */
  {
    id: 'vectors-rn',
    title: 'Vectors in n Dimensions',
    summary: 'An arrow was only ever a picture of a list of numbers — and the list works in dimensions no picture reaches.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['vectors'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'linear-combinations',
    title: 'Linear Combinations',
    summary: 'Scale some vectors, add them up, and ask which vectors you can build that way.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['vectors-rn'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'span',
    title: 'Span',
    summary: 'Everything reachable from a handful of vectors — a point, a line, a plane, or the whole space.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['linear-combinations'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'linear-independence',
    title: 'Linear Independence',
    summary: 'Whether every vector in a set is pulling its weight, or one of them was already reachable from the rest.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['span'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ------------------------------------------------------------- matrices */
  {
    id: 'matrices',
    title: 'Matrices and Matrix Operations',
    summary: 'A rectangle of numbers, and the one multiplication rule in mathematics that is not commutative.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['vectors-rn'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'row-reduction',
    title: 'Row Reduction and Echelon Form',
    summary: 'Three moves that change a matrix without changing what it says, run until the answer is visible.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['matrices'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'gaussian-elimination',
    title: 'Gaussian Elimination',
    summary: 'Solving any number of equations in any number of unknowns, by one procedure that always terminates.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['row-reduction', 'systems-of-equations'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'matrix-inverse',
    title: 'Inverse Matrices',
    summary: 'Undoing a matrix — when it can be done at all, and why most matrices cannot be undone.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['gaussian-elimination'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'determinants',
    title: 'Determinants',
    summary: 'One number that reports whether a matrix is invertible — and, geometrically, what it does to area.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['matrix-inverse'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* -------------------------------------------------------- linear systems */
  {
    id: 'linear-systems',
    title: 'Systems in Matrix Form',
    summary: 'Every system of linear equations written as one equation, Ax = b — and read two completely different ways.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['gaussian-elimination', 'linear-combinations'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'rank',
    title: 'Rank',
    summary: 'How much independent information a matrix actually carries, counted by its pivots.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['linear-systems', 'linear-independence'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'solution-sets',
    title: 'The Shape of a Solution Set',
    summary: 'None, exactly one, or infinitely many — never two — and the reason a solution set is a plane moved off the origin.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['rank'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* --------------------------------------------------------- vector spaces */
  {
    id: 'vector-spaces',
    title: 'Vector Spaces',
    summary: 'Eight rules that ℝⁿ obeys — and so do polynomials, matrices and functions, which is why they are studied together.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['linear-independence', 'solution-sets'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'subspaces',
    title: 'Subspaces',
    summary: 'A vector space living inside another one, and the three-line test that decides whether a set is one.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['vector-spaces'],
    status: 'available',
    estimatedMinutes: 40,
  },
  {
    id: 'basis',
    title: 'Basis',
    summary: 'The smallest set that still reaches everything — independent and spanning, neither alone being enough.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['subspaces'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'dimension',
    title: 'Dimension and Rank–Nullity',
    summary: 'Every basis of a space has the same size, so a space has a number — and that number is conserved.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['basis', 'rank'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'coordinates',
    title: 'Coordinates and Change of Basis',
    summary: 'The same vector written in two languages, and the matrix that translates between them.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['basis'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* -------------------------------------------------- linear transformations */
  {
    id: 'linear-transformations',
    title: 'Linear Transformations',
    summary: 'Functions that respect addition and scaling — which is to say, every function a matrix can be.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['vector-spaces', 'matrices'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'kernel-range',
    title: 'Kernel and Range',
    summary: 'What a transformation destroys and what it reaches — and why those two sizes always add to the same total.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['linear-transformations', 'dimension'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'transformation-matrix',
    title: 'The Matrix of a Transformation',
    summary: 'Where the basis vectors land is the whole of a linear map — so the matrix is read off, not derived.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['linear-transformations', 'coordinates'],
    status: 'available',
    estimatedMinutes: 45,
  },

  /* ---------------------------------------------- eigenvalues and eigenvectors */
  {
    id: 'eigenvalues',
    title: 'Eigenvalues',
    summary: 'The scale factors a transformation applies along the directions it does not turn.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['determinants', 'transformation-matrix'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'eigenvectors',
    title: 'Eigenvectors and Eigenspaces',
    summary: 'The directions themselves — found by solving a system that is guaranteed, by construction, to be degenerate.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['eigenvalues', 'kernel-range'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'diagonalization',
    title: 'Diagonalization',
    summary: 'Rewriting a matrix in the basis of its own eigenvectors, where it becomes multiplication by a list of numbers.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['eigenvectors', 'basis'],
    status: 'available',
    estimatedMinutes: 45,
  },
  {
    id: 'eigen-applications',
    title: 'Eigenvalues at Work',
    summary: 'Powers of a matrix, populations that settle into a fixed ratio, and the long run of a random process.',
    strand: 'linear-algebra',
    levels: ['university'],
    prerequisites: ['diagonalization'],
    status: 'available',
    estimatedMinutes: 45,
  },
];
